import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { captureApolloError } from "services/external/sentry";
import type { Fridge } from "types";
import { networkTrace, shouldTraceNetworkDebug } from "utils/debug/networkTrace";
import { INITIAL_STATE_QUERY } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";

type ReconnectArgs = {
	isSubscriptionBroken: boolean;
	appConnected: boolean;
	fridgeId: string;
	refetch: () => Promise<{ data?: { fridge?: Fridge | null } }>;
	onData: (fridge: Fridge) => void;
	/**
	 * Re-subscribes the live FRIDGE_SUBSCRIPTION (Apollo useSubscription's restart()).
	 * The AppSync link errors the observable on a dropped socket and never re-opens it, and
	 * RetryLink only retries a few times before giving up — so on recovery the refetch below
	 * restores initial state but the live stream stays dead unless we restart it here too.
	 */
	restartSubscription: () => void;
};

/**
 * Owns the "re-fetch initial fridge state after the live subscription broke" retry.
 *
 * Defects fixed vs. the old inline DeviceContext logic:
 * - The retry setTimeout is tracked in a ref and cleared on cleanup, so it can't fire after unmount.
 * - The reconnect function is stabilized with useCallback and guarded by isReconnectingRef, so the
 *   effect runs one retry chain per outage instead of re-firing on every provider re-render.
 * - onData is kept current via a ref, so a fresh onData identity each render does NOT re-trigger the
 *   reconnect effect.
 */
export const useReconnectAfterBrokenSubscription = ({
	isSubscriptionBroken,
	appConnected,
	fridgeId,
	refetch,
	onData,
	restartSubscription,
}: ReconnectArgs): void => {
	const onDataRef = useRef(onData);
	const restartSubscriptionRef = useRef(restartSubscription);
	const retryTimeoutRef = useRef<number | null>(null);
	const isReconnectingRef = useRef(false);

	useEffect(() => {
		onDataRef.current = onData;
	}, [onData]);

	// Kept in a ref so a fresh restartSubscription identity each render (it depends on
	// shouldSkipSubscription in the provider) does NOT re-trigger the recovery effect.
	useEffect(() => {
		restartSubscriptionRef.current = restartSubscription;
	}, [restartSubscription]);

	const handleReconnectAfterBrokenSubscription = useCallback(
		async (retryCount: number): Promise<void> => {
			if (retryCount > 2) {
				captureApolloError(new Error("Failed to load initial fridge state 3 times"), {
					kind: "Initial_Query_Error_Retries_Limit",
					query: INITIAL_STATE_QUERY.kind,
					variables: { fridgeId: fridgeId },
				});
				return;
			}
			try {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[ws] reconnect: refetch attempt #${retryCount + 1}`);
				}
				logToSentry(`Sending initial query after broken subscription on fridge: ${fridgeId}`, "warning");
				const res = await refetch();
				if (res.data?.fridge) {
					if (shouldTraceNetworkDebug()) {
						networkTrace("[ws] reconnect: refetch ok → initial state restored");
					}
					logToSentry(`Setting initial data after broken subscription fridge: ${fridgeId}`, "warning", [
						res.data.fridge,
					]);
					onDataRef.current(res.data.fridge);
				} else {
					if (shouldTraceNetworkDebug()) {
						networkTrace(`[ws] reconnect: refetch empty (retry #${retryCount + 1})`);
					}
					captureApolloError(
						new Error("Initial query error"),
						{
							kind: "Initial_Query_Error",
							query: INITIAL_STATE_QUERY.kind,
							variables: { fridgeId: fridgeId },
						},
						{
							ResponseData: res,
						},
					);
					retryTimeoutRef.current = window.setTimeout(() => {
						handleReconnectAfterBrokenSubscription(retryCount + 1);
					}, 1000);
				}
			} catch (err) {
				Sentry.captureException(err);
			}
		},
		[fridgeId, refetch],
	);

	useEffect(() => {
		if (isSubscriptionBroken && appConnected && fridgeId && !isReconnectingRef.current) {
			isReconnectingRef.current = true;
			// Resume the live stream (a new AppSync handshake); the first frame that arrives
			// clears isSubscriptionBroken via onData. Done before/independent of the refetch so
			// the live subscription recovers even if the initial-query refetch is still retrying.
			if (shouldTraceNetworkDebug()) {
				networkTrace("[ws] reconnect: restart subscription");
			}
			restartSubscriptionRef.current();
			handleReconnectAfterBrokenSubscription(0);
		}
		if (!isSubscriptionBroken) {
			isReconnectingRef.current = false;
		}
	}, [isSubscriptionBroken, appConnected, fridgeId, handleReconnectAfterBrokenSubscription]);

	useEffect(
		() => () => {
			if (retryTimeoutRef.current !== null) {
				window.clearTimeout(retryTimeoutRef.current);
				retryTimeoutRef.current = null;
			}
		},
		[],
	);
};
